import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Shield, MapPin, Globe, Phone, Loader2, CheckCircle2 } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { useToast } from '@/components/ui/use-toast';
import { cn } from '@/lib/utils';

interface SuggestClubModalProps {
  isOpen: boolean;
  onClose: () => void;
  city: string;
}

const SuggestClubModal: React.FC<SuggestClubModalProps> = ({ isOpen, onClose, city }) => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    city: city || '',
    website: '',
    phone: '',
    description: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Clear error when field is edited
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Club name is required';
    }
    
    // Website validation (optional field)
    if (formData.website && !formData.website.match(/^(https?:\/\/)?(www\.)?[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}(\/.*)?$/)) {
      newErrors.website = 'Please enter a valid website URL';
    }
    
    // Phone validation (optional field)
    if (formData.phone && !formData.phone.match(/^[+]?[(]?[0-9]{1,4}[)]?[-\s./0-9]*$/)) {
      newErrors.phone = 'Please enter a valid phone number';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setIsSubmitting(true);
    
    try {
      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');
      
      // Get user's database ID
      const { data: userData, error: userError } = await supabase
        .from('users')
        .select('id')
        .eq('auth_id', user.id)
        .single();
      
      if (userError) throw userError;
      
      // Submit club suggestion
      const { error: submitError } = await supabase
        .from('club_suggestions')
        .insert({
          name: formData.name,
          address: formData.address,
          city: formData.city,
          website: formData.website || null,
          phone: formData.phone || null,
          description: formData.description || null,
          suggested_by: userData.id
        });
      
      if (submitError) throw submitError;
      
      // Show success state
      setIsSuccess(true);
      
      // Reset form after 3 seconds and close modal
      setTimeout(() => {
        setFormData({
          name: '',
          address: '',
          city: city || '',
          website: '',
          phone: '',
          description: ''
        });
        setIsSuccess(false);
        onClose();
        
        toast({
          title: "Club suggestion submitted",
          description: "Thank you for your contribution! We'll review your suggestion soon.",
          variant: "default",
        });
      }, 2000);
      
    } catch (error) {
      console.error('Error submitting club suggestion:', error);
      toast({
        title: "Error",
        description: "Failed to submit club suggestion. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[550px] bg-dark-200 border-dark-300 text-white">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <Shield className="h-5 w-5 text-gold-400" />
            Suggest a New Club
          </DialogTitle>
        </DialogHeader>
        
        {isSuccess ? (
          <div className="py-8 text-center">
            <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="h-8 w-8 text-green-500" />
            </div>
            <h3 className="text-xl font-semibold text-white mb-2">Thank You!</h3>
            <p className="text-dark-800 max-w-md mx-auto">
              Your club suggestion has been submitted successfully. We'll review it and add it to our database soon.
            </p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name" className="text-dark-800">
                Club Name <span className="text-red-500">*</span>
              </Label>
              <Input
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="Enter club name"
                className={cn(
                  "bg-dark-300 border-dark-400 text-white",
                  errors.name && "border-red-500"
                )}
              />
              {errors.name && <p className="text-red-500 text-xs">{errors.name}</p>}
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="address" className="text-dark-800">
                Address
              </Label>
              <Input
                id="address"
                name="address"
                value={formData.address}
                onChange={handleChange}
                placeholder="Enter club address"
                className="bg-dark-300 border-dark-400 text-white"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="city" className="text-dark-800">
                City
              </Label>
              <Input
                id="city"
                name="city"
                value={formData.city}
                onChange={handleChange}
                placeholder="Enter city"
                className="bg-dark-300 border-dark-400 text-white"
              />
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="website" className="text-dark-800 flex items-center">
                  <Globe className="h-3.5 w-3.5 mr-1" /> Website
                </Label>
                <Input
                  id="website"
                  name="website"
                  value={formData.website}
                  onChange={handleChange}
                  placeholder="https://example.com"
                  className={cn(
                    "bg-dark-300 border-dark-400 text-white",
                    errors.website && "border-red-500"
                  )}
                />
                {errors.website && <p className="text-red-500 text-xs">{errors.website}</p>}
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="phone" className="text-dark-800 flex items-center">
                  <Phone className="h-3.5 w-3.5 mr-1" /> Phone
                </Label>
                <Input
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="+1 (123) 456-7890"
                  className={cn(
                    "bg-dark-300 border-dark-400 text-white",
                    errors.phone && "border-red-500"
                  )}
                />
                {errors.phone && <p className="text-red-500 text-xs">{errors.phone}</p>}
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="description" className="text-dark-800">
                Description
              </Label>
              <Textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                placeholder="Tell us about this club..."
                className="bg-dark-300 border-dark-400 text-white min-h-[100px]"
              />
            </div>
            
            <DialogFooter className="pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                className="border-dark-400"
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="bg-gold-400 hover:bg-gold-500 text-dark-50"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  'Submit Suggestion'
                )}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default SuggestClubModal;